import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";
import {
    createVehicle,
    getVehicleById,
    updateVehicle,
    uploadVehiclePhoto,
    getServiceLocations,
    getVehicleMakes,
    getVehicleModels,
    getFuelTypes,
    getFuelIssues,
    getStatuses,
    getCompanies,
    getTransmissionTypes,
    getTaxStatuses,
    getFinancialYears,
} from "../../services/vehicle_service";
import { getCurrentFinancialYear } from "../../services/auth_service";
import { isAdmin, isSuperadmin, hasPermission } from "../utils/permissions";
import "./AddVehicle.css";
const INITIAL_FORM = {
    vehicle_code: "",
    service_location_id: "",
    company_id: "",
    vehicle_make_id: "",
    vehicle_model_id: "",
    fuel_type_id: "",
    transmission_type_id: "",
    tax_status_id: "",
    status_id: "",
    fuel_issue_id: "",
    vehicle_registration_number: "",
    vehicle_display_number: "",
    registration_date: "",
    engine_number: "",
    chassis_number: "",
    average_mileage: "",
    year_of_make: "",
    seating_capacity: "",
    gps_enabled: false,
    vehicle_photo: "",
    financial_year_id: null,
};
export default function AddVehicle() {
    const navigate = useNavigate();
    const { id } = useParams();
    const confirm = useConfirm();
    const isEdit = Boolean(id);
    const isAdminUser = isAdmin();
    const isSuperadminUser = isSuperadmin();
    const canEdit =
        isSuperadminUser ||
        isAdminUser ||
        hasPermission("vehicles", "edit");
    const readOnly = isEdit && !canEdit;
    const mastersLoaded = useRef(false);
    const [serviceLocations, setServiceLocations] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [vehicleMakes, setVehicleMakes] = useState([]);
    const [vehicleModels, setVehicleModels] = useState([]);
    const [fuelTypes, setFuelTypes] = useState([]);
    const [fuelIssues, setFuelIssues] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [transmissionTypes, setTransmissionTypes] = useState([]);
    const [taxStatuses, setTaxStatuses] = useState([]);
    const [financialYears, setFinancialYears] = useState([]);
    const [currentFY, setCurrentFY] = useState(null);
    const [form, setForm] = useState(INITIAL_FORM);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    useEffect(() => {
        if (form.vehicle_make_id) {
            getVehicleModels(form.vehicle_make_id).then(setVehicleModels);
        } else {
            setVehicleModels([]);
        }
    }, [form.vehicle_make_id]);
    useEffect(() => {
        if (!id && serviceLocations.length === 1 && !form.service_location_id) {
            setForm((f) => ({ ...f, service_location_id: String(serviceLocations[0].id) }));
        }
    }, [serviceLocations, id, form.service_location_id]);
    const locationIsScoped = serviceLocations.length === 1;
    const handleBack = async () => {
        if (readOnly) {
            navigate("/vehicles");
            return;
        }
        const confirmed = await confirm({
            title: "Unsaved Changes",
            message: "Are you sure you want to go back? Any unsaved changes will be lost.",
            confirmLabel: "Go Back",
            cancelLabel: "Stay on Page",
            danger: true,
        });
        if (confirmed) {
            navigate("/vehicles");
        }
    };
    const loadMasters = useCallback(async () => {
        const [
            locations, companyData, makes,
            fuelData, issueData, statusData,
            transmissionData, taxData,
            fyListData, currentFyData,
        ] = await Promise.allSettled([
            getServiceLocations(),
            getCompanies(),
            getVehicleMakes(),
            getFuelTypes(),
            getFuelIssues(),
            getStatuses(),
            getTransmissionTypes(),
            getTaxStatuses(),
            getFinancialYears(),
            getCurrentFinancialYear(),
        ]);
        if (locations.status === "fulfilled") setServiceLocations(locations.value);
        if (companyData.status === "fulfilled") setCompanies(companyData.value);
        if (makes.status === "fulfilled") setVehicleMakes(makes.value);
        if (fuelData.status === "fulfilled") setFuelTypes(fuelData.value);
        if (issueData.status === "fulfilled") setFuelIssues(issueData.value);
        if (statusData.status === "fulfilled") setStatuses(statusData.value);
        if (transmissionData.status === "fulfilled") setTransmissionTypes(transmissionData.value);
        if (taxData.status === "fulfilled") setTaxStatuses(taxData.value);
        if (fyListData.status === "fulfilled") setFinancialYears(fyListData.value || []);
        if (currentFyData.status === "fulfilled") setCurrentFY(currentFyData.value);
    }, []);
    const loadVehicle = useCallback(async () => {
        try {
            const data = await getVehicleById(id);
            setForm({
                ...data,
                vehicle_code: data.vehicle_code ?? "",
                service_location_id: String(data.service_location_id ?? ""),
                company_id: String(data.company_id ?? ""),
                vehicle_make_id: String(data.vehicle_make_id ?? ""),
                vehicle_model_id: String(data.vehicle_model_id ?? ""),
                fuel_type_id: String(data.fuel_type_id ?? ""),
                transmission_type_id: String(data.transmission_type_id ?? ""),
                tax_status_id: String(data.tax_status_id ?? ""),
                status_id: String(data.status_id ?? ""),
                fuel_issue_id: String(data.fuel_issue_id ?? ""),
                financial_year_id: data.financial_year_id ?? null,
            });
            if (data.vehicle_make_id) {
                const models = await getVehicleModels(data.vehicle_make_id);
                setVehicleModels(models);
            }
            if (data.vehicle_photo) setPhotoPreview(data.vehicle_photo);
        } catch (err) {
            console.error("Load vehicle error:", err);
        }
    }, [id]);
    useEffect(() => {
        if (!mastersLoaded.current) {
            mastersLoaded.current = true;
            loadMasters();
        }
        if (id) loadVehicle();
    }, [id, loadMasters, loadVehicle]);
    const field = (key, value) => {
        if (readOnly) return;
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: "" }));
    };
    const handlePhotoChange = (e) => {
        if (readOnly) return;
        const file = e.target.files[0];
        if (!file) return;
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
    };
    const validate = () => {
        const newErrors = {};
        if (!form.service_location_id) {
            newErrors.service_location_id = "Service Location is required.";
        }
        if (!form.company_id) {
            newErrors.company_id = "Company is required.";
        }
        if (!form.vehicle_registration_number) {
            newErrors.vehicle_registration_number = "Vehicle Registration Number is required.";
        } else if (!/^[A-Z0-9]{1,12}$/.test(form.vehicle_registration_number)) {
            newErrors.vehicle_registration_number =
                "Registration Number must be up to 12 characters (uppercase letters and numbers only).";
        }
        if (!form.vehicle_display_number) {
            newErrors.vehicle_display_number = "Vehicle Display Number is required.";
        } else if (form.vehicle_display_number.length !== 4) {
            newErrors.vehicle_display_number = "Display Number must be exactly 4 characters (last 4 digits).";
        } else if (
            form.vehicle_registration_number &&
            form.vehicle_registration_number.slice(-4) !== form.vehicle_display_number
        ) {
            newErrors.vehicle_display_number =
                `Display Number must match the last 4 digits of the Registration Number (${form.vehicle_registration_number.slice(-4)}).`;
        }
        if (!form.registration_date) {
            newErrors.registration_date = "Registration Date is required.";
        }
        if (!form.vehicle_make_id) {
            newErrors.vehicle_make_id = "Vehicle Make is required.";
        }
        if (!form.vehicle_model_id) {
            newErrors.vehicle_model_id = "Vehicle Model is required.";
        }
        if (!form.engine_number) {
            newErrors.engine_number = "Engine Number is required.";
        } else if (!/^[A-Z0-9]{1,25}$/.test(form.engine_number)) {
            newErrors.engine_number =
                "Engine Number must be up to 25 characters using only letters and numbers (no spaces).";
        }
        if (!form.chassis_number) {
            newErrors.chassis_number = "Chassis Number is required.";
        } else if (!/^[A-Z0-9]{1,25}$/.test(form.chassis_number)) {
            newErrors.chassis_number =
                "Chassis Number must be up to 25 characters using only letters and numbers (no spaces).";
        }
        if (
            form.engine_number &&
            form.chassis_number &&
            form.engine_number === form.chassis_number
        ) {
            newErrors.chassis_number = "Chassis Number cannot be the same as the Engine Number.";
        }
        if (!form.year_of_make) {
            newErrors.year_of_make = "Year of Make is required.";
        } else if (!/^\d{4}$/.test(String(form.year_of_make))) {
            newErrors.year_of_make = "Year of Make must be exactly 4 digits.";
        }
        if (!form.transmission_type_id) {
            newErrors.transmission_type_id = "Transmission Type is required.";
        }
        if (!form.seating_capacity) {
            newErrors.seating_capacity = "Seating Capacity is required.";
        }
        if (!form.tax_status_id) {
            newErrors.tax_status_id = "Tax Status is required.";
        }
        if (!form.fuel_type_id) {
            newErrors.fuel_type_id = "Fuel Type is required.";
        }
        if (!form.average_mileage) {
            newErrors.average_mileage = "Average Mileage is required.";
        }
        if (!form.fuel_issue_id) {
            newErrors.fuel_issue_id = "Fuel Issue is required.";
        }
        if (!form.status_id) {
            newErrors.status_id = "Vehicle Status is required.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (readOnly) return;
        if (!validate()) {
            const firstErrorEl = document.querySelector(".field-error");
            if (firstErrorEl) firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }
        setSubmitting(true);
        try {
            let photoPath = form.vehicle_photo;
            if (photoFile) {
                const uploadResult = await uploadVehiclePhoto(photoFile);
                photoPath = uploadResult.vehicle_photo;
            }
            const { vehicle_code, ...formWithoutCode } = form;
            const payload = {
                ...formWithoutCode,
                vehicle_photo: photoPath,
                service_location_id: Number(form.service_location_id) || null,
                company_id: Number(form.company_id) || null,
                vehicle_make_id: Number(form.vehicle_make_id) || null,
                vehicle_model_id: Number(form.vehicle_model_id) || null,
                fuel_type_id: Number(form.fuel_type_id) || null,
                transmission_type_id: Number(form.transmission_type_id) || null,
                tax_status_id: Number(form.tax_status_id) || null,
                status_id: Number(form.status_id) || null,
                fuel_issue_id: Number(form.fuel_issue_id) || null,
                average_mileage: form.average_mileage ? Number(form.average_mileage) : null,
                year_of_make: form.year_of_make ? Number(form.year_of_make) : null,
                seating_capacity: form.seating_capacity ? Number(form.seating_capacity) : null,
            };
            if (!id) delete payload.financial_year_id;
            if (id) {
                await updateVehicle(id, payload);
                alert("Vehicle Updated Successfully");
            } else {
                await createVehicle(payload);
                alert("Vehicle Created Successfully");
            }
            sessionStorage.removeItem("vehicles");
            navigate("/vehicles");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            let message = "Failed To Save Vehicle";
            if (typeof detail === "string") {
                message = detail;
            } else if (Array.isArray(detail)) {
                message = detail.map((d) => d.msg).filter(Boolean).join("\n") || message;
            }
            alert(message);
        } finally {
            setSubmitting(false);
        }
    };
    const scopedLocationLabel =
        serviceLocations.find((x) => String(x.id) === String(form.service_location_id))?.name
        || (serviceLocations[0]?.name)
        || "Detecting…";
    return (
        <div className="layout">
            <Sidebar />
            <div className="content">
                <Header />
                <div className="add-vehicle-page">
                    <div className="vehicle-card">
                        <div className="vehicle-card-header">
                            <div className="vehicle-card-heading">
                                <h2>{id ? (readOnly ? "View Vehicle" : "Edit Vehicle") : "Add Vehicle"}</h2>
                                <span className="vehicle-card-subtitle">
                                    {readOnly
                                        ? "Viewing vehicle details (read-only)"
                                        : id
                                            ? "Update vehicle details and documents"
                                            : "Fill in vehicle details in the order below"}
                                </span>
                            </div>
                            {id && form.vehicle_code && (
                                <span className="vehicle-code-badge" title="Auto-generated vehicle code">
                                    {form.vehicle_code}
                                </span>
                            )}
                        </div>
                        <form className="vehicle-form" onSubmit={handleSubmit} noValidate>
                            <fieldset disabled={readOnly} style={{ border: "none", padding: 0, margin: 0 }}>
                                {id && form.vehicle_code && (
                                    <div className="form-field">
                                        <label className="field-label">Vehicle Code</label>
                                        <input
                                            type="text"
                                            value={form.vehicle_code}
                                            disabled
                                            className="input-readonly"
                                        />
                                    </div>
                                )}
                                <div className="form-section-title"><span className="section-num">01</span>Location & Company</div>
                                <div className="form-field">
                                    {locationIsScoped ? (
                                        <>
                                            <label className="field-label">Service Location</label>
                                            <input
                                                type="text"
                                                readOnly
                                                className="input-readonly"
                                                value={scopedLocationLabel}
                                                title="Auto-assigned from your account — not editable here."
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <select
                                                value={form.service_location_id}
                                                onChange={(e) => field("service_location_id", e.target.value)}
                                                className={errors.service_location_id ? "input-error" : ""}
                                            >
                                                <option value="">Select Service Location *</option>
                                                {serviceLocations.map((x) => (
                                                    <option key={x.id} value={String(x.id)}>{x.name}</option>
                                                ))}
                                            </select>
                                            {errors.service_location_id && (
                                                <span className="field-error">{errors.service_location_id}</span>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className="form-field">
                                    <select
                                        value={form.company_id}
                                        onChange={(e) => field("company_id", e.target.value)}
                                        className={errors.company_id ? "input-error" : ""}
                                    >
                                        <option value="">Select Company *</option>
                                        {companies.map((x) => (
                                            <option key={x.id} value={String(x.id)}>{x.name}</option>
                                        ))}
                                    </select>
                                    {errors.company_id && (
                                        <span className="field-error">{errors.company_id}</span>
                                    )}
                                </div>
                                <div className="form-section-title"><span className="section-num">02</span>Registration</div>
                                <div className="form-field">
                                    <input
                                        type="text"
                                        placeholder="Vehicle Registration Number (up to 12 chars) *"
                                        value={form.vehicle_registration_number}
                                        maxLength={12}
                                        className={errors.vehicle_registration_number ? "input-error" : ""}
                                        onChange={(e) =>
                                            field("vehicle_registration_number", e.target.value.toUpperCase())
                                        }
                                    />
                                    <span className="char-count">
                                        {form.vehicle_registration_number.length}/12
                                    </span>
                                    {errors.vehicle_registration_number && (
                                        <span className="field-error">{errors.vehicle_registration_number}</span>
                                    )}
                                </div>
                                <div className="form-field">
                                    <input
                                        type="text"
                                        placeholder="Vehicle Display Number (Last 4 Digits) *"
                                        maxLength={4}
                                        value={form.vehicle_display_number}
                                        className={errors.vehicle_display_number ? "input-error" : ""}
                                        onChange={(e) => field("vehicle_display_number", e.target.value)}
                                    />
                                    <span className="char-count">
                                        {form.vehicle_display_number.length}/4
                                    </span>
                                    {errors.vehicle_display_number && (
                                        <span className="field-error">{errors.vehicle_display_number}</span>
                                    )}
                                </div>
                                <div className="form-field">
                                    <label className="field-label">
                                        Registration Date <span className="req">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={form.registration_date}
                                        min="1980-01-01"
                                        max={new Date().toISOString().split("T")[0]}
                                        className={errors.registration_date ? "input-error" : ""}
                                        onChange={(e) => field("registration_date", e.target.value)}
                                    />
                                    {errors.registration_date && (
                                        <span className="field-error">{errors.registration_date}</span>
                                    )}
                                </div>
                                <div className="form-section-title"><span className="section-num">03</span>Make & Model</div>
                                <div className="form-field">
                                    <select
                                        value={form.vehicle_make_id}
                                        onChange={(e) => {
                                            field("vehicle_make_id", e.target.value);
                                            field("vehicle_model_id", "");
                                        }}
                                        className={errors.vehicle_make_id ? "input-error" : ""}
                                    >
                                        <option value="">Select Vehicle Make *</option>
                                        {vehicleMakes.map((x) => (
                                            <option key={x.id} value={String(x.id)}>{x.name}</option>
                                        ))}
                                    </select>
                                    {errors.vehicle_make_id && (
                                        <span className="field-error">{errors.vehicle_make_id}</span>
                                    )}
                                </div>
                                <div className="form-field">
                                    <select
                                        value={form.vehicle_model_id}
                                        onChange={(e) => field("vehicle_model_id", e.target.value)}
                                        disabled={readOnly || !form.vehicle_make_id}
                                        className={errors.vehicle_model_id ? "input-error" : ""}
                                    >
                                        <option value="">
                                            {form.vehicle_make_id ? "Select Vehicle Model *" : "Select Make First"}
                                        </option>
                                        {vehicleModels.map((x) => (
                                            <option key={x.id} value={String(x.id)}>{x.name}</option>
                                        ))}
                                    </select>
                                    {errors.vehicle_model_id && (
                                        <span className="field-error">{errors.vehicle_model_id}</span>
                                    )}
                                </div>
                                <div className="form-section-title"><span className="section-num">04</span>Engine & Chassis</div>
                                <div className="form-field">
                                    <input
                                        type="text"
                                        placeholder="Engine Number *"
                                        value={form.engine_number}
                                        maxLength={25}
                                        className={errors.engine_number ? "input-error" : ""}
                                        onChange={(e) =>
                                            field(
                                                "engine_number",
                                                e.target.value
                                                    .toUpperCase()
                                                    .replace(/\s/g, "")
                                                    .replace(/[^A-Z0-9]/g, "")
                                            )
                                        }
                                    />
                                    <span className="char-count">
                                        {form.engine_number.length}/25
                                    </span>
                                    {errors.engine_number && (
                                        <span className="field-error">{errors.engine_number}</span>
                                    )}
                                </div>
                                <div className="form-field">
                                    <input
                                        type="text"
                                        placeholder="Chassis Number *"
                                        value={form.chassis_number}
                                        maxLength={25}
                                        className={errors.chassis_number ? "input-error" : ""}
                                        onChange={(e) =>
                                            field(
                                                "chassis_number",
                                                e.target.value
                                                    .toUpperCase()
                                                    .replace(/\s/g, "")
                                                    .replace(/[^A-Z0-9]/g, "")
                                            )
                                        }
                                    />
                                    <span className="char-count">
                                        {form.chassis_number.length}/25
                                    </span>
                                    {errors.chassis_number && (
                                        <span className="field-error">{errors.chassis_number}</span>
                                    )}
                                </div>
                                <div className="form-section-title"><span className="section-num">05</span>Fuel & Performance</div>
                                <div className="form-field">
                                    <select
                                        value={form.fuel_type_id}
                                        onChange={(e) => field("fuel_type_id", e.target.value)}
                                        className={errors.fuel_type_id ? "input-error" : ""}
                                    >
                                        <option value="">Select Fuel Type *</option>
                                        {fuelTypes.map((x) => (
                                            <option key={x.id} value={String(x.id)}>{x.name}</option>
                                        ))}
                                    </select>
                                    {errors.fuel_type_id && (
                                        <span className="field-error">{errors.fuel_type_id}</span>
                                    )}
                                </div>
                                <div className="form-field">
                                    <input
                                        type="number"
                                        placeholder="Average Mileage *"
                                        value={form.average_mileage}
                                        className={errors.average_mileage ? "input-error" : ""}
                                        onChange={(e) => field("average_mileage", e.target.value)}
                                    />
                                    {errors.average_mileage && (
                                        <span className="field-error">{errors.average_mileage}</span>
                                    )}
                                </div>
                                <div className="form-field">
                                    <select
                                        value={form.fuel_issue_id}
                                        onChange={(e) => field("fuel_issue_id", e.target.value)}
                                        className={errors.fuel_issue_id ? "input-error" : ""}
                                    >
                                        <option value="">Select Fuel Issue *</option>
                                        {fuelIssues.map((x) => (
                                            <option key={x.id} value={String(x.id)}>{x.name}</option>
                                        ))}
                                    </select>
                                    {errors.fuel_issue_id && (
                                        <span className="field-error">{errors.fuel_issue_id}</span>
                                    )}
                                </div>
                                <div className="form-section-title"><span className="section-num">06</span>Vehicle Details</div>
                                <div className="form-field">
                                    <input
                                        type="number"
                                        placeholder="Year of Make (4 digits) *"
                                        value={form.year_of_make}
                                        maxLength={4}
                                        className={errors.year_of_make ? "input-error" : ""}
                                        onChange={(e) => field("year_of_make", e.target.value)}
                                    />
                                    {errors.year_of_make && (
                                        <span className="field-error">{errors.year_of_make}</span>
                                    )}
                                </div>
                                <div className="form-field">
                                    <select
                                        value={form.transmission_type_id}
                                        onChange={(e) => field("transmission_type_id", e.target.value)}
                                        className={errors.transmission_type_id ? "input-error" : ""}
                                    >
                                        <option value="">Select Transmission Type *</option>
                                        {transmissionTypes.map((x) => (
                                            <option key={x.id} value={String(x.id)}>{x.name}</option>
                                        ))}
                                    </select>
                                    {errors.transmission_type_id && (
                                        <span className="field-error">{errors.transmission_type_id}</span>
                                    )}
                                </div>
                                <div className="form-field">
                                    <input
                                        type="number"
                                        placeholder="Seating Capacity *"
                                        value={form.seating_capacity}
                                        className={errors.seating_capacity ? "input-error" : ""}
                                        onChange={(e) => field("seating_capacity", e.target.value)}
                                    />
                                    {errors.seating_capacity && (
                                        <span className="field-error">{errors.seating_capacity}</span>
                                    )}
                                </div>
                                <div className="form-field">
                                    <select
                                        value={form.tax_status_id}
                                        onChange={(e) => field("tax_status_id", e.target.value)}
                                        className={errors.tax_status_id ? "input-error" : ""}
                                    >
                                        <option value="">Select Tax Status *</option>
                                        {taxStatuses.map((x) => (
                                            <option key={x.id} value={String(x.id)}>{x.name}</option>
                                        ))}
                                    </select>
                                    {errors.tax_status_id && (
                                        <span className="field-error">{errors.tax_status_id}</span>
                                    )}
                                </div>
                                <label className="gps-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={form.gps_enabled}
                                        onChange={(e) => field("gps_enabled", e.target.checked)}
                                    />
                                    GPS Enabled
                                </label>
                                <div className="form-section-title"><span className="section-num">07</span>Status & Photo</div>
                                <div className="form-field">
                                    <select
                                        value={form.status_id}
                                        onChange={(e) => field("status_id", e.target.value)}
                                        className={errors.status_id ? "input-error" : ""}
                                    >
                                        <option value="">Select Status *</option>
                                        {statuses.map((x) => (
                                            <option key={x.id} value={String(x.id)}>{x.name}</option>
                                        ))}
                                    </select>
                                    {errors.status_id && (
                                        <span className="field-error">{errors.status_id}</span>
                                    )}
                                </div>
                                <div className="photo-upload">
                                    <label className="photo-label">Vehicle Photo</label>
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={handlePhotoChange}
                                    />
                                    {photoPreview && (
                                        <img
                                            src={photoPreview}
                                            alt="Vehicle Preview"
                                            className="photo-preview"
                                        />
                                    )}
                                </div>
                            </fieldset>
                            <div className="form-actions">
                                <button type="button" className="btn-secondary" onClick={handleBack}>
                                    Back
                                </button>
                                {!readOnly && (
                                    <button type="submit" className="submit-btn" disabled={submitting}>
                                            <span className="submit-btn-text">
                                                {submitting
                                                    ? "Saving..."
                                                    : id ? "Update Vehicle" : "Create Vehicle"}
                                            </span>
                                            {!submitting && <span className="submit-btn-arrow" aria-hidden="true">→</span>}
                                        </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
